import { cache, loadModule } from '@weareduet/core';
import 'lazysizes';

import SetVh from '@/globals/SetVh';

import '@/plugins/logger';
import webfontloader from '@/plugins/webfontloader';
import detectTab from '@/utils/detectTab';
import activeLinks from '@/utils/activeLinks';
import localLinks from '@/utils/localLinks';
import {
	destroyBehavioursWithin,
	destroyModule,
	hydrateBehaviours,
} from '@/utils/sponLifecycle';
import { showMessage, hideMessage } from './ui/messages';

const globalModuleIds = [];

function mountGlobalModule(config) {
	if (cache.has(config.id)) return;

	globalModuleIds.push(config.id);
	loadModule(config);
}

webfontloader();

if (import.meta.env.DEV) {
	document.documentElement.classList.add('is-dev');
}

// load from data-behaviours
hydrateBehaviours(document.body);

mountGlobalModule({
	module: SetVh,
	id: 'set-vh',
	node: document.body,
	keepAlive: true,
});

detectTab();
activeLinks(window.location.pathname);
localLinks();

function handleBeforeSend() {
	hideMessage();

	const results = document.getElementById('results');
	if (results) {
		results.classList.add('is-loading');
	}
}

function handleBeforeSwap({
	detail: {
		requestConfig: { triggeringEvent },
	},
	target,
}) {
	destroyBehavioursWithin(target);

	if (triggeringEvent) {
		const srcElement =
			triggeringEvent.srcElement ?? triggeringEvent.target ?? null;
		if (!srcElement) return;

		const shouldScroll = srcElement.closest('.c-pagination');
		if (srcElement && shouldScroll) {
			window.scrollTo({
				top: target.getBoundingClientRect().top + window.scrollY - 100,
				behavior: 'smooth',
			});
		}
	}
}

function handleAfterSwap({ target }) {
	const results = document.getElementById('results');
	if (results) {
		results.classList.remove('is-loading');
	}

	localLinks(target);
	activeLinks(window.location.pathname);
	hydrateBehaviours(target);
}

function handleResponseError() {
	showMessage('Something went wrong, please try again.');
}

// https://htmx.org/events
if (typeof htmx !== 'undefined') {
	htmx.on('htmx:beforeSend', handleBeforeSend);

	// Scroll to top of `sprig` container when interacting; but only when `scroll` is in Param (eg not filtering; but probably pagination)
	htmx.on('htmx:beforeSwap', handleBeforeSwap);

	// Used if sprig target != results; dont' rely on DOM change to remove `.is-loading`
	htmx.on('htmx:afterSwap', handleAfterSwap);

	htmx.on('htmx:responseError', handleResponseError);
}

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		if (typeof htmx !== 'undefined' && typeof htmx.off === 'function') {
			htmx.off('htmx:beforeSend', handleBeforeSend);
			htmx.off('htmx:beforeSwap', handleBeforeSwap);
			htmx.off('htmx:afterSwap', handleAfterSwap);
			htmx.off('htmx:responseError', handleResponseError);
		}

		destroyBehavioursWithin(document.body);
		globalModuleIds.forEach(id => destroyModule(id));
	});
}
